// The `web_search` tool returns search-result XML on success, but plain text on
// every failure path (see WebSearchService.InvokeWebSearchFunctionAsync). Those
// strings are written for the model, so they leak raw provider/exception detail
// ("Status InternalServerError - Response status code does not indicate success:
// 500 (INTERNAL SERVER ERROR).") which reads to a user as if the app itself broke.
//
// This maps a plain-text output to a short, human explanation that makes clear the
// fault sits with the external search provider. The original text is still shown,
// tucked behind a "Show details" collapse, so it stays available for support.

export type WebSearchOutputKind = 'note' | 'cancelled' | 'error';

export interface WebSearchOutputStatus {
    kind: WebSearchOutputKind;
    /** Headline shown in place of the raw message. */
    title: string;
    /** One-line explanation of what happened and what to do about it. */
    detail: string;
}

// "InternalServerError" -> "internal server error"; "TooManyRequests" -> "too many requests".
const humaniseStatusName = (status: string): string =>
    status
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .trim();

const RETRY_HINT = 'Try asking again in a moment.';

const describeHttpStatus = (status: string): string => {
    const normalised = status.toLowerCase();

    if (
        normalised === 'unauthorized' ||
        normalised === 'forbidden' ||
        normalised === 'paymentrequired'
    ) {
        return 'The search provider rejected the request, which usually means an account or API key problem. This needs an administrator to look at it.';
    }

    if (normalised === 'toomanyrequests') {
        return `The search provider is rate limiting requests right now. ${RETRY_HINT}`;
    }

    if (
        normalised === 'internalservererror' ||
        normalised === 'badgateway' ||
        normalised === 'serviceunavailable' ||
        normalised === 'gatewaytimeout'
    ) {
        return `The search provider had a problem on its end (${humaniseStatusName(status)}). Nothing is wrong with your assistant. ${RETRY_HINT}`;
    }

    return `The search provider returned an error (${humaniseStatusName(status)}). ${RETRY_HINT}`;
};

/**
 * Classify a non-XML `web_search` tool output. Returns `note` for benign
 * messages (e.g. "No search results found."), which callers should render as
 * plain muted text rather than as a failure.
 */
export const getWebSearchOutputStatus = (
    output: string
): WebSearchOutputStatus => {
    const text = output.trim();

    if (/cancelled|canceled/i.test(text)) {
        return {
            kind: 'cancelled',
            title: 'Web search cancelled',
            detail: 'The search was stopped before it finished.'
        };
    }

    if (!/^error/i.test(text) && !/error occurred/i.test(text)) {
        return {kind: 'note', title: '', detail: ''};
    }

    const failed: WebSearchOutputStatus = {
        kind: 'error',
        title: 'Web search unavailable',
        detail: `The search couldn't be completed. ${RETRY_HINT}`
    };

    if (/timed out/i.test(text)) {
        return {
            ...failed,
            title: 'Web search timed out',
            detail: `The search provider took too long to respond. ${RETRY_HINT}`
        };
    }

    const httpStatus = text.match(/Status\s+([A-Za-z]+)/);
    if (httpStatus) {
        return {...failed, detail: describeHttpStatus(httpStatus[1])};
    }

    if (/could not understand the response/i.test(text)) {
        return {
            ...failed,
            detail: `The search provider sent back a response we couldn't read. ${RETRY_HINT}`
        };
    }

    if (/^error fetching web search results/i.test(text)) {
        return {
            ...failed,
            title: "Couldn't reach the search provider",
            detail: `The search service couldn't be contacted. ${RETRY_HINT}`
        };
    }

    if (
        /search query cannot be empty|processing web search arguments/i.test(
            text
        )
    ) {
        return {
            ...failed,
            title: 'Web search request was invalid',
            detail: `The assistant built a search request that couldn't be run. ${RETRY_HINT}`
        };
    }

    return failed;
};
